import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  DEFAULT_NGX_DATATABLE_CONFIG,
  DEFAULT_NGX_DATATABLE_PAGINATION,
} from '../../helpers/ui/ngx-datatable.constant';
import {
  ApiData,
  ApiResponse,
} from '../../interfaces/api/api-response.interface';
import {
  NgxDatatableConfig,
  NgxDatatableSort,
  SortFilter,
} from '../../interfaces/ui/ngx-datatable.interface';
import { FilterService } from './filter.service';

const DEFAULT_RESPONSE = {
  statusCode: 0,
  status: '',
  message: '',
  data: {
    result: [],
    totalCount: 0,
  },
};

type HandleFilter = { keepPagination?: boolean; keepSort?: boolean };
type ObservableResponse<D> = Observable<ApiResponse<ApiData<D[]>>>;
type SearchFunction<D, F> = (filter: F) => ObservableResponse<D>;

@Injectable()
export class NgxDatatableService<D, F extends HandleFilter>  implements OnDestroy {
  private defaultLimit = DEFAULT_NGX_DATATABLE_PAGINATION.LIMIT;
  private defaultPage = DEFAULT_NGX_DATATABLE_PAGINATION.PAGE;
  private defaultSort: NgxDatatableSort | undefined = undefined;
  private isFirstLoadConfig = true;
  private filter: F = {} as F;

  private dataSearchFunction: SearchFunction<D, F> = () => of(DEFAULT_RESPONSE);
  private config: BehaviorSubject<NgxDatatableConfig> = this.setDefaultConfig();
  private cleanup$ = new Subject<void>();

  public config$ = this.config.asObservable().pipe(takeUntil(this.cleanup$));
  public data$ = this.setTableData().pipe(takeUntil(this.cleanup$));

  // TODO: Implementar nuevo servico de exportación
  constructor(
    private _filterService: FilterService<F | null>,
    private _translateService: TranslateService
  ) {
    this._filterService.updateFilterDataBehavior(null);
  }

  private setDefaultConfig(): BehaviorSubject<NgxDatatableConfig> {
    return new BehaviorSubject<NgxDatatableConfig>(
      DEFAULT_NGX_DATATABLE_CONFIG
    );
  }

  private setTableData(): Observable<D[]> {
    return this._filterService.getFilterDataBehavior().pipe(
      filter((filter) => !!filter),
      switchMap((filter) => this.fetchData(this.handleFilter(filter!)))
    );
  }

  private fetchData(filter: F): Observable<D[]> {
    this.setTableConfig({ loadingIndicator: true });
    return this.dataSearchFunction(filter).pipe(
      tap((res) => this.handleFetchSuccess(res)),
      map((res) => res.data.result),
      catchError((err) => this.handleFetchError(err))
    );
  }

  private handleFetchSuccess(res: ApiResponse<ApiData<D[]>>): void {
    const totalCount = res.data.totalCount;
    this.setTableConfig({ count: totalCount, loadingIndicator: false });
  }

  private handleFetchError(err: any): Observable<D[]> {
    this.setTableConfig({ count: 0, loadingIndicator: false });
    return of([]);
  }

  private handleFilter(filter: F): F {
    this.filter = filter;

    const { limit, page } = this.configurePagination(filter);
    const sort = this.configureSorting(filter);

    delete filter.keepPagination;
    delete filter.keepSort;

    return { ...this.filter, ...sort, limit, page };
  }

  private configurePagination(filter: F): { limit: number; page: number } {
    if (!filter.keepPagination) {
      const pagination = { limit: this.defaultLimit, page: this.defaultPage };
      this.setTableConfig(pagination);
    }

    const limit = this.config.value.limit;
    const page = this.config.value.page;

    return { limit, page };
  }

  private configureSorting(filter: F): SortFilter | undefined {
    if (this.isFirstLoadConfig) {
      this.isFirstLoadConfig = false;
      this.defaultSort = this.config.value.sort;
    }

    if (!filter.keepSort) this.setTableConfig({ sort: this.defaultSort });

    if (!this.config.value.sort) return;

    return this.handleFilterSorting(this.config.value.sort);
  }

  private handleFilterSorting(sort: NgxDatatableSort): SortFilter {
    const { prop: sortProp, dir: sortDir } = sort;
    let handleSortFilter: SortFilter = { sortProp, sortDir };
    if (this.config.value.responseSortType === 'number') {
      handleSortFilter.sortDir = sortDir === 'asc' ? 1 : -1;
    }

    return handleSortFilter;
  }

  private handleFilterData(
    filter: F,
    keepPagination: boolean = true,
    keepSort: boolean = true
  ): void {
    this._filterService.updateFilterDataBehavior({
      ...filter,
      keepPagination,
      keepSort,
    });
  }

  // * PUBLIC METHODS

  public setDataSearchFunction(fetchDataFunction: SearchFunction<D, F>): void {
    this.dataSearchFunction = fetchDataFunction;
  }

  public setTableConfig(config: Partial<NgxDatatableConfig>): void {
    this.config.next({ ...this.config.value, ...config });
  }

  public reloadDatatable(): void {
    this.handleFilterData(this.filter);
  }

  public changeLimit(limit: number): void {
    this.setTableConfig({ limit, page: this.defaultPage });
    this.handleFilterData(this.filter);
  }

  public changePage(page: number): void {
    this.setTableConfig({ page });
    this.handleFilterData(this.filter);
  }

  public sort(sort: NgxDatatableSort): void {
    this.setTableConfig({ sort });
    this.handleFilterData(this.filter);
  }

  ngOnDestroy(): void {
    this.config.complete();
    this.cleanup$.next();
    this.cleanup$.complete();
  }
}
